/**
 * Route-level error boundary — wraps `<Outlet />` in `AppLayout` so a
 * render-time throw inside any routed page (MissionControl, workshop
 * dashboards, NotFound) degrades to a panel-styled fallback rather
 * than blowing up the entire React tree.
 *
 * React's error boundary API is class-only — `componentDidCatch` /
 * `getDerivedStateFromError` have no hook equivalent. To use the
 * `useNavigate` and `useLocation` router hooks we wrap the class
 * component in `RouteErrorBoundary` (function) and pass `navigate` +
 * `pathname` as props. On pathname change the wrapper bumps a `key`
 * that resets the boundary's internal state, so a user who navigates
 * away from a broken route isn't trapped on the stale fallback.
 *
 * Fallback chrome reuses the existing `.modal-shell` / `.modal-header`
 * / `.modal-body` / `.modal-close-btn` tokens (see `src/index.css`
 * lines 864-910) so the failure surface reads as a first-class panel.
 * The error message itself is rendered in a `<pre>` tinted with the
 * `--accent-danger` token (added in the earlier accent-danger work).
 */

import { Component, type ReactNode } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

interface BoundaryProps {
  children: ReactNode;
  navigate: NavigateFunction;
}

interface BoundaryState {
  error: Error | null;
}

class RouteErrorBoundaryInner extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface to the console so devs see the full stack even though
    // the UI swallows the throw. Production telemetry hookup is out
    // of scope for Phase 5b; revisit when error reporting lands.
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary] route render failed", error, info);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleHome = (): void => {
    this.props.navigate("/");
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "48px 24px",
        }}
      >
        <div
          className="modal-shell"
          style={{ width: "100%", maxWidth: 640 }}
          role="alert"
        >
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <AlertTriangle
                aria-hidden
                style={{
                  width: 16,
                  height: 16,
                  color: "var(--accent-red)",
                }}
              />
              <span className="modal-title">Something broke</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={this.handleHome}
                className="modal-close-btn"
                aria-label="Back to Mission Control"
              >
                <Home aria-hidden style={{ width: 12, height: 12 }} />
                BACK TO MISSION CONTROL
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="modal-close-btn"
                aria-label="Reload page"
              >
                <RotateCcw aria-hidden style={{ width: 12, height: 12 }} />
                RELOAD PAGE
              </button>
            </div>
          </div>
          <div className="modal-body">
            <div style={{ padding: "16px 20px" }}>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "var(--fs-sub)",
                  lineHeight: 1.6,
                  marginBottom: 12,
                }}
              >
                Something broke while rendering this view. Reload the page
                to recover, or head back to Mission Control to keep working.
              </p>
              <pre
                style={{
                  margin: 0,
                  padding: "10px 12px",
                  background: "rgba(var(--accent-danger-rgb), 0.10)",
                  border: "1px solid var(--accent-danger)",
                  borderRadius: 6,
                  color: "var(--text-primary)",
                  fontSize: "var(--fs-sub)",
                  fontFamily: "var(--font-mono)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {error.message || String(error)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Public wrapper. Threads `useNavigate` + `useLocation` into the class
 * component, and uses the current pathname as a React `key` so the
 * boundary remounts (and clears its `error` state) whenever the route
 * changes. That way a thrown render on `/workshop/foo` doesn't keep
 * the fallback pinned after the user clicks back to `/`.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundaryInner key={pathname} navigate={navigate}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
